import React from "react";
import {
  composeRenderProps,
  type FieldErrorProps,
  Group,
  GroupContext,
  type GroupProps,
  LabelContext,
  type LabelProps,
  FieldError as RACFieldError,
  Input as RACInput,
  type InputProps as RACInputProps,
  Label as RACLabel,
  Text as RACText,
  TextArea as RACTextArea,
  type TextAreaProps as RACTextAreaProps,
  TextField as RACTextField,
  type TextFieldProps as RACTextFieldProps,
  type TextProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Text } from "@/components/ui/text";
import {
  cn,
  composeTailwindRenderProps,
  type DisplayLevel,
  displayLevels,
  inputFocusRing,
} from "@/components/ui/utils";

// https://react-spectrum.adobe.com/react-aria/Group.html#advanced-customization
export function LabeledGroup({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  const labelId = React.useId();

  return (
    <LabelContext.Provider value={{ id: labelId, elementType: "span" }}>
      <GroupContext.Provider value={{ "aria-labelledby": labelId }}>
        <div
          className={cn(
            ["[&>[data-ui=label]:first-of-type:not([class*=mb])]:mb-2"],
            className,
          )}
        >
          {children}
        </div>
      </GroupContext.Provider>
    </LabelContext.Provider>
  );
}

export function Label({
  requiredHint,
  displayLevel = 3,
  ...props
}: LabelProps & {
  requiredHint?: boolean;
  displayLevel?: DisplayLevel;
}) {
  return (
    <RACLabel
      {...props}
      data-ui="label"
      className={cn(
        "inline-block min-w-max text-pretty",
        "group-disabled:opacity-50",
        displayLevels[displayLevel],
        requiredHint &&
          "after:ms-0.5 after:text-destructive after:content-['*']",
        props.className,
      )}
    />
  );
}

const DescriptionContext = React.createContext<{
  "aria-describedby"?: string;
} | null>(null);

export function DescriptionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const descriptionId: string | null = React.useId();
  const [descriptionRendered, setDescriptionRendered] = React.useState(true);

  React.useLayoutEffect(() => {
    if (!document.getElementById(descriptionId)) {
      setDescriptionRendered(false);
    }
  }, [descriptionId]);

  return (
    <DescriptionContext.Provider
      value={{
        "aria-describedby": descriptionRendered ? descriptionId : undefined,
      }}
    >
      {children}
    </DescriptionContext.Provider>
  );
}

export function useDescription() {
  return React.useContext(DescriptionContext);
}

/**
 * RAC will auto associate <RACText slot="description"/> with TextField/NumberField/RadioGroup/CheckboxGroup/DatePicker etc,
 * but not for Switch/Checkbox/Radio and our custom components. We use follow pattern to associate description for
 * Switch/Checkbox/Radio https://react-spectrum.adobe.com/react-aria/Switch.html#advanced-customization
 */
export function Description({ className, ...props }: TextProps) {
  const describedby = useDescription()?.["aria-describedby"];

  return describedby ? (
    <Text
      {...props}
      id={describedby}
      data-ui="description"
      slot="description"
      className={cn(
        "block text-muted-foreground group-disabled:opacity-50",
        className,
      )}
    />
  ) : (
    <RACText
      {...props}
      data-ui="description"
      slot="description"
      className={cn(
        "block text-pretty text-muted-foreground text-sm",
        "group-disabled:opacity-50",
        className,
      )}
    />
  );
}

export function TextField(props: RACTextFieldProps) {
  return (
    <RACTextField
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group *:not-first:mt-1",
      )}
    />
  );
}

export function FieldError(props: FieldErrorProps) {
  return (
    <RACFieldError
      {...props}
      className={composeRenderProps(props.className, (className) =>
        cn("block text-destructive text-sm", className),
      )}
    />
  );
}

export const inputStyles = tv({
  extend: inputFocusRing,
  base: [
    "flex h-8 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-xs shadow-black/5 transition-shadow placeholder:text-muted-foreground/70",
  ],
  variants: {
    type: {
      search:
        "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
      file: "p-0 pr-3 italic text-muted-foreground/70 file:me-3 file:h-full file:border-0 file:border-r file:border-solid file:border-input file:bg-transparent file:px-3 file:text-sm file:font-medium file:not-italic file:text-foreground",
    },
    isInvalid: {
      true: "border-destructive",
    },
    isDisabled: {
      true: "opacity-50 cursor-not-allowed",
    },
  },
});

export type InputProps = RACInputProps & {
  ref?: React.Ref<HTMLInputElement | null>;
};

export function Input({ className, ref, type, ...props }: InputProps) {
  return (
    <RACInput
      {...props}
      ref={ref}
      type={type}
      className={composeRenderProps(className, (className, renderProps) =>
        inputStyles({
          type: type as "search" | "file",
          className,
          ...renderProps,
        }),
      )}
    />
  );
}

export function UnstyledInput({ className, ref, ...props }: InputProps) {
  return (
    <RACInput
      {...props}
      ref={ref}
      className={composeTailwindRenderProps(
        className,
        "px-2 flex-1 min-w-0 outline-0 bg-background text-sm",
      )}
    />
  );
}

const textAreaStyles = tv({
  extend: inputFocusRing,
  base: [
    "flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm shadow-black/5 transition-shadow placeholder:text-muted-foreground/70",
  ],
  variants: {
    isDisabled: {
      true: "opacity-50 cursor-not-allowed",
    },
    isInvalid: {
      true: "border-destructive",
    },
  },
});

export function TextArea(
  props: RACTextAreaProps & { ref?: React.Ref<HTMLTextAreaElement> },
) {
  return (
    <RACTextArea
      {...props}
      ref={props.ref}
      className={composeRenderProps(props.className, (className, renderProps) =>
        textAreaStyles({
          className,
          ...renderProps,
        }),
      )}
    />
  );
}

const fieldBorderStyles = tv({
  variants: {
    isFocusWithin: {
      true: "border-ring outline-hidden ring-[3px] ring-ring/20",
    },
    isInvalid: {
      true: "border-destructive ring-destructive/20",
    },
    isDisabled: {
      true: "border-input opacity-50",
    },
  },
});

const fieldGroupStyles = tv({
  extend: inputFocusRing,
  base: "group flex items-center h-8 bg-background border border-input rounded-lg overflow-hidden text-sm text-foreground shadow-xs shadow-black/5 transition-shadow",
  variants: fieldBorderStyles.variants,
});

export function FieldGroup(props: GroupProps) {
  return (
    <Group
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        fieldGroupStyles({ ...renderProps, className }),
      )}
    />
  );
}
